import React, { memo, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getNodeConfig } from './nodeConfig';
import { useNovelty, noveltyColor } from './NoveltyContext';

const IdeaNode = memo(({ data }) => {
  const [isHovered, setIsHovered] = useState(false);
  const novelty = useNovelty();
  const nodeId = data.nodeId || data.id;
  const noveltyEntry = novelty.scores[nodeId];
  const noveltyScore = noveltyEntry?.score;
  const noveltyCell = novelty.cells[nodeId];
  // Filter check: dim nodes that don't match active novelty filters
  const f = novelty.filters;
  const filterActive = f.minNovelty > 0 || f.cellMechanism || f.cellRisk || f.cellAudience || f.cellHorizon;
  let noveltyFilterMatch = true;
  if (filterActive) {
    if (f.minNovelty > 0 && (noveltyScore == null || noveltyScore < f.minNovelty)) noveltyFilterMatch = false;
    if (f.cellMechanism && noveltyCell?.mechanism !== f.cellMechanism) noveltyFilterMatch = false;
    if (f.cellRisk && noveltyCell?.risk !== f.cellRisk) noveltyFilterMatch = false;
    if (f.cellAudience && noveltyCell?.audience !== f.cellAudience) noveltyFilterMatch = false;
    if (f.cellHorizon && noveltyCell?.horizon !== f.cellHorizon) noveltyFilterMatch = false;
  }
  const config = getNodeConfig(data.type, data.dynamicConfig);
  const isSelected = data.isSelected;
  const isStarred = data.starred;
  const isInRange = data.isInRange !== false;
  const searchActive = data.searchActive === true;
  const searchMatch = data.searchMatch !== false;
  const dimmedBySearch = searchActive && !searchMatch;
  const chatFilterActive = data.chatFilterActive === true;
  const chatFilterMatch = data.chatFilterMatch !== false;
  const dimmedByChatFilter = chatFilterActive && !chatFilterMatch;
  const dimmedByNoveltyFilter = filterActive && !noveltyFilterMatch;
  const isDimmed = dimmedBySearch || dimmedByChatFilter || dimmedByNoveltyFilter;
  const isUnexplored = (data.childCount === 0) && !data.expanded && !data.isExpanding;
  const isLeaf = data.childCount === 0;
  const isConvergence = (data.parentIds?.length || 0) > 1;
  const hasLoop = !!data.loopId;
  const isGhost = data.isGhost === true;
  const isMergeTarget = data.isMergeTarget === true;
  // Synaptic
  const confidence = data.confidence ?? 0.7;
  const hasStreams = (data.streams?.length || 0) > 0;
  const signalPulse = data.signalPulse; // 'reinforces' | 'contradicts' | null

  // Show detail on hover or selection
  const showDetail = isHovered || isSelected;

  // Build box shadow
  let boxShadow;
  if (isMergeTarget) {
    boxShadow = '0 0 0 2px #818cf8, 0 0 20px rgba(129,140,248,0.4)';
  } else if (isStarred) {
    boxShadow = '0 0 0 2px #ffd43b, 0 0 28px rgba(255,212,59,0.3)';
  } else if (isSelected) {
    boxShadow = `0 0 0 2px ${config.color}, 0 0 24px ${config.glow}`;
  } else if (isHovered) {
    boxShadow = `0 0 0 1px ${config.color}, 0 0 16px ${config.glow}`;
  } else {
    boxShadow = `0 0 8px ${config.glow}, 0 2px 6px rgba(0,0,0,0.3)`;
  }
  if (isUnexplored && isInRange) {
    boxShadow += ', 0 4px 14px rgba(108,99,255,0.2)';
  }
  // Novelty glow — outer halo colored by score (red/yellow/green)
  if (typeof noveltyScore === 'number' && !isMergeTarget && !isStarred) {
    const novColor = noveltyColor(noveltyScore);
    const intensity = Math.max(0.15, noveltyScore / 100 * 0.4);
    boxShadow += `, 0 0 24px ${novColor}${Math.round(intensity * 255).toString(16).padStart(2,'0')}`;
  }

  // Confidence as subtle background tint (replaces the bar)
  const confAlpha = Math.round(confidence * 0.08 * 100) / 100;
  const confTint = confidence > 0.6
    ? `rgba(34,197,94,${confAlpha})`
    : confidence > 0.4
    ? `rgba(250,204,21,${confAlpha})`
    : `rgba(248,113,113,${confAlpha})`;

  return (
    <div
      className={[
        'idea-node-diet',
        isGhost ? 'idea-node-ghost' : null,
        signalPulse === 'reinforces' ? 'signal-pulse-reinforce' : null,
        signalPulse === 'contradicts' ? 'signal-pulse-contradict' : null,
      ].filter(Boolean).join(' ')}
      onMouseEnter={() => {
        setIsHovered(true);
        data.onHoverPreview?.(data.nodeId, true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        data.onHoverPreview?.(data.nodeId, false);
      }}
      style={{
        background: isGhost
          ? 'rgba(139,92,246,0.05)'
          : `linear-gradient(135deg, ${config.bg}, ${confTint})`,
        border: `1px solid ${isMergeTarget ? '#818cf8' : isGhost ? 'rgba(139,92,246,0.2)' : isStarred ? '#ffd43b' : isSelected ? config.color : config.border}`,
        borderRadius: '8px',
        padding: '10px 12px',
        width: '220px',
        minHeight: isGhost ? '50px' : '68px',
        position: 'relative',
        fontFamily: 'var(--font-mono)',
        cursor: isInRange ? 'pointer' : 'default',
        boxShadow,
        transition: 'all 0.2s ease',
        opacity: isGhost ? 0.6 : isDimmed ? 0.35 : (isInRange ? 1 : 0.08),
        filter: isDimmed ? 'saturate(0.4) brightness(0.7)' : (isInRange ? 'none' : 'saturate(0.2)'),
        pointerEvents: isGhost ? 'none' : isDimmed ? 'none' : (isInRange ? 'auto' : 'none'),
      }}
    >
      {/* Novelty score badge — top right */}
      {typeof noveltyScore === 'number' && !isGhost && (
        <div
          title={noveltyEntry?.closestExisting ? `Novelty ${noveltyScore}/100 · vs ${noveltyEntry.closestExisting}` : `Novelty ${noveltyScore}/100`}
          style={{
            position: 'absolute',
            top: -7,
            right: -7,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: noveltyColor(noveltyScore),
            color: '#0a0a0a',
            fontSize: 10,
            fontWeight: 800,
            fontFamily: 'var(--font-mono)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #0a0a0a',
            zIndex: 10,
            boxShadow: `0 0 8px ${noveltyColor(noveltyScore)}80`,
          }}
        >
          {noveltyScore}
        </div>
      )}

      {/* Ghost shimmer overlay */}
      {isGhost && (
        <div className="ghost-shimmer">
          <div style={{ color: '#a78bfa', fontSize: 11, fontWeight: 600 }}>
            Generating…
          </div>
          <div className="ghost-pulse-bar" />
        </div>
      )}

      {/* Left accent bar — always visible, colored by type */}
      <div style={{
        position: 'absolute', left: 0, top: '8px', bottom: '8px',
        width: data.executionStatus ? '4px' : (hasLoop ? '4px' : '3px'),
        background: data.executionStatus === 'in_progress' ? '#f59e0b'
          : data.executionStatus === 'completed' ? '#22c55e'
          : data.executionStatus === 'failed' ? '#f87171'
          : hasLoop ? (data.loopType === 'balancing' ? '#f59e0b' : '#22c55e')
          : config.color,
        borderRadius: '0 2px 2px 0',
        opacity: data.executionStatus || hasLoop ? 1 : 0.85,
      }} />

      {/* ── ALWAYS VISIBLE ─────────────────────────────── */}

      {/* Star badge — always visible when starred */}
      {isStarred && (
        <div style={{
          position: 'absolute', top: 6, right: 8,
          color: '#ffd43b', fontSize: 11, lineHeight: 1,
        }}>★</div>
      )}

      {/* Type badge + label inline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
        <span style={{ color: config.color, fontSize: '11px', flexShrink: 0 }}>{config.icon}</span>
        <span style={{
          color: config.color, fontSize: '9px', fontWeight: '700',
          letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.9,
          flexShrink: 0,
        }}>
          {config.label}
        </span>
      </div>

      {/* Label — always visible, 2 lines max */}
      <div
        style={{
          color: '#e8e8f0', fontSize: '12px', fontWeight: '600',
          lineHeight: '1.35', letterSpacing: '0.01em',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
        title={data.label}
      >
        {data.label}
      </div>

      {/* ── HOVER/SELECT REVEAL ────────────────────────── */}

      {/* Reasoning — hover only */}
      {showDetail && data.reasoning && (
        <div
          style={{
            color: '#8888b8', fontSize: '10px', lineHeight: '1.45',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            paddingTop: '6px', marginTop: '6px', fontStyle: 'italic',
            display: '-webkit-box', WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            animation: 'nodeDetailReveal 0.15s ease-out',
          }}
          title={data.reasoning}
        >
          {data.reasoning}
        </div>
      )}

      {/* Score badge — hover only */}
      {showDetail && data.score != null && (
        <div style={{
          position: 'absolute', top: 6, right: isStarred ? 24 : 8,
          display: 'flex', alignItems: 'center', padding: '1px 5px',
          borderRadius: 4, fontSize: 9, fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          background: data.score >= 8 ? 'rgba(34,197,94,0.15)' : data.score >= 5 ? 'rgba(250,204,21,0.15)' : 'rgba(248,113,113,0.15)',
          color: data.score >= 8 ? '#22c55e' : data.score >= 5 ? '#facc15' : '#f87171',
          border: `1px solid ${data.score >= 8 ? 'rgba(34,197,94,0.3)' : data.score >= 5 ? 'rgba(250,204,21,0.3)' : 'rgba(248,113,113,0.3)'}`,
          animation: 'nodeDetailReveal 0.15s ease-out',
        }}>
          {data.score}/10
        </div>
      )}

      {/* Mastery badge — hover only (learn mode) */}
      {showDetail && data.mastery != null && data.mastery > 0 && (
        <div style={{
          position: 'absolute', top: 6,
          right: data.score != null ? (isStarred ? 60 : 44) : (isStarred ? 24 : 8),
          display: 'flex', alignItems: 'center', padding: '1px 5px',
          borderRadius: 4, fontSize: 9, fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          background: data.mastery >= 8 ? 'rgba(34,197,94,0.15)' : data.mastery >= 5 ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.15)',
          color: data.mastery >= 8 ? '#22c55e' : data.mastery >= 5 ? '#fbbf24' : '#ef4444',
          border: `1px solid ${data.mastery >= 8 ? 'rgba(34,197,94,0.3)' : data.mastery >= 5 ? 'rgba(251,191,36,0.3)' : 'rgba(239,68,68,0.3)'}`,
          animation: 'nodeDetailReveal 0.15s ease-out',
        }}>
          {data.mastery >= 8 ? '✓' : data.mastery >= 5 ? '◐' : '○'} {data.mastery}/10
        </div>
      )}

      {/* Mastery left accent bar (learn mode) */}
      {data.mastery != null && (
        <div style={{
          position: 'absolute', top: 0, left: 0, bottom: 0, width: 4,
          borderRadius: '8px 0 0 8px',
          background: data.mastery >= 8 ? '#22c55e' : data.mastery >= 5 ? '#fbbf24' : data.mastery >= 1 ? '#ef4444' : '#374151',
        }} />
      )}

      {/* Depth indicator — hover only */}
      {showDetail && data.depth >= 2 && (
        <span style={{
          position: 'absolute', bottom: 6, right: 8,
          fontSize: 8, fontWeight: 600, color: '#555580',
          letterSpacing: '0.04em', opacity: 0.7,
          fontFamily: 'var(--font-mono)',
        }}>
          L{data.depth}
        </span>
      )}

      {/* Stream indicator — hover only */}
      {showDetail && hasStreams && (
        <div
          title={`${data.streams.length} data stream${data.streams.length > 1 ? 's' : ''} connected`}
          className="stream-indicator"
          style={{
            position: 'absolute', bottom: 6, right: data.depth >= 2 ? 30 : 8,
            fontSize: 9, fontWeight: 700, color: '#38bdf8',
            fontFamily: 'var(--font-mono)',
            display: 'flex', alignItems: 'center', gap: 2,
          }}
        >
          <span style={{ fontSize: 10, lineHeight: 1 }}>&#x21AF;</span>
          {data.streams.length}
        </div>
      )}

      {/* Execution status badge — hover only */}
      {showDetail && data.executionStatus === 'in_progress' && (
        <div className="node-exec-badge node-exec-pulse" style={{
          position: 'absolute', top: 6, right: 8,
        }}>
          ⟳ FIXING…
        </div>
      )}
      {showDetail && data.executionStatus === 'completed' && (
        <div className="node-exec-badge node-exec-fixed" style={{
          position: 'absolute', top: 6, right: 8,
        }}>
          ✓ FIXED
        </div>
      )}
      {showDetail && data.executionStatus === 'failed' && (
        <div className="node-exec-badge node-exec-failed" style={{
          position: 'absolute', top: 6, right: 8,
        }}>
          ✗ FAILED
        </div>
      )}

      {/* Merge target indicator — hover only */}
      {showDetail && isMergeTarget && (
        <div style={{
          position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
          background: '#1e1e2e', border: '1px solid #818cf8',
          borderRadius: 8, padding: '1px 8px',
          fontSize: 8, fontWeight: 700, color: '#818cf8',
          fontFamily: 'var(--font-mono)', zIndex: 3,
          letterSpacing: '0.06em',
        }}>
          MERGE TARGET
        </div>
      )}

      {/* Convergence badge — hover only */}
      {showDetail && isConvergence && (
        <div
          title={`Convergence: ${data.parentIds.length} parent connections`}
          style={{
            position: 'absolute', top: -10, right: 12,
            background: '#1e1e2e', border: '1px solid #818cf8',
            borderRadius: 8, padding: '1px 6px',
            fontSize: 9, fontWeight: 700, color: '#818cf8',
            fontFamily: 'var(--font-mono)', zIndex: 3,
            display: 'flex', alignItems: 'center', gap: 3,
            animation: 'nodeDetailReveal 0.15s ease-out',
          }}
        >
          <span style={{ fontSize: 10 }}>&#x21E4;</span> {data.parentIds.length}
        </div>
      )}

      {/* Loop badge — hover only */}
      {showDetail && hasLoop && (
        <div
          title={`Part of feedback loop: ${data.loopId}`}
          style={{
            position: 'absolute', top: -10, left: 12,
            background: '#1e1e2e',
            border: `1px solid ${data.loopType === 'balancing' ? '#f59e0b' : '#22c55e'}`,
            borderRadius: 8, padding: '1px 6px',
            fontSize: 8, fontWeight: 700,
            color: data.loopType === 'balancing' ? '#f59e0b' : '#22c55e',
            fontFamily: 'var(--font-mono)', zIndex: 3,
            letterSpacing: '0.05em',
            animation: 'nodeDetailReveal 0.15s ease-out',
          }}
        >
          {data.loopType === 'balancing' ? '⟳ BAL' : '⟲ REINF'}
        </div>
      )}

      {/* Pattern badge — hover only */}
      {showDetail && data.patternMeta && (
        <div
          className="node-pattern-badge"
          title={`Pattern: ${data.patternMeta.name}`}
          style={{
            position: 'absolute', bottom: -10, left: 12,
            background: '#1e1e2e',
            border: `1px solid ${data.patternMeta.color || '#6c63ff'}44`,
            borderRadius: 8, padding: '1px 6px',
            fontSize: 8, fontWeight: 700,
            color: data.patternMeta.color || '#6c63ff',
            fontFamily: 'var(--font-mono)', zIndex: 3,
            letterSpacing: '0.05em',
            animation: 'nodeDetailReveal 0.15s ease-out',
          }}
        >
          {data.patternMeta.icon} {data.patternMeta.name}
        </div>
      )}

      {/* Lens — hover only */}
      {showDetail && data.lens && (
        <span
          title={data.lens === 'first_principles' ? 'First Principles' : data.lens === 'analogical' ? 'Analogical' : data.lens === 'adversarial' ? 'Adversarial' : 'Synthesis'}
          style={{
            position: 'absolute', top: 6, right: 8,
            fontSize: 8, fontWeight: 600,
            letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.7,
            color: data.lens === 'analogical' ? '#4dabf7' : data.lens === 'first_principles' ? '#69db7c' : data.lens === 'adversarial' ? '#ff6b6b' : '#c084fc',
          }}
        >
          {data.lens === 'first_principles' ? 'FP' : data.lens === 'analogical' ? 'AN' : data.lens === 'adversarial' ? 'ADV' : 'SYN'}
        </span>
      )}

      {/* Auto-explored badge — hover only */}
      {showDetail && data.autoExplored && (
        <span
          title="Auto-explored by fractal mode"
          style={{
            position: 'absolute', bottom: 6, left: 10,
            fontSize: 10, color: '#c084fc', opacity: 0.8,
            fontWeight: 700,
          }}
        >
          ∞
        </span>
      )}

      {/* ── ALWAYS VISIBLE: Handles ────────────────────── */}

      <Handle type="target" position={Position.Top} style={{
        background: config.color, width: 8, height: 8,
        border: `2px solid ${config.bg}`, top: -5,
      }} />
      <Handle type="source" position={Position.Bottom} style={{
        background: config.color, width: 8, height: 8,
        border: `2px solid ${config.bg}`, bottom: -5,
      }} />

      {/* Mnemonic video button — learn mode only, concept/prerequisite/milestone */}
      {data.onGenerateMnemonic && ['concept', 'prerequisite', 'milestone'].includes(data.type) && (
        <button
          className={`mnemonic-btn ${data.mnemonicStatus === 'generating' || data.mnemonicStatus === 'polling' ? 'generating' : ''} ${data.mnemonicStatus === 'complete' ? 'ready' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (data.mnemonicStatus === 'complete') {
              data.onPlayMnemonic?.(data.nodeId);
            } else if (!data.mnemonicStatus || data.mnemonicStatus === 'error') {
              data.onGenerateMnemonic(data.nodeId);
            }
          }}
          title={data.mnemonicStatus === 'complete' ? 'Play mnemonic video' : data.mnemonicStatus === 'generating' || data.mnemonicStatus === 'polling' ? 'Generating video...' : 'Generate memory mnemonic video'}
          style={{
            position: 'absolute', top: 6, left: 8, zIndex: 3,
          }}
        >
          {data.mnemonicStatus === 'complete' ? '▶' : '🎬'}
        </button>
      )}

      {/* ── ALWAYS VISIBLE: Bottom controls ────────────── */}

      {data.isExpanding ? (
        <div className="fractal-expanding" style={{
          position: 'absolute', bottom: -20, left: '50%',
          transform: 'translateX(-50%)',
        }}>
          <span className="fractal-pulse" />
        </div>
      ) : isLeaf && isInRange && !isDimmed ? (
        <button
          className="fractal-expand-btn"
          onClick={(e) => { e.stopPropagation(); data.onFractalExpand?.(data.nodeId); }}
          title="Expand this idea fractally"
          style={{
            position: 'absolute', bottom: -14, left: '50%',
            transform: 'translateX(-50%)',
            background: '#16161f', border: `1px solid ${config.border}`,
            borderRadius: 12, padding: '2px 10px',
            fontSize: 12, fontWeight: 700, color: '#6c63ff',
            cursor: 'pointer', zIndex: 2, fontFamily: 'var(--font-mono)',
            transition: 'all 0.2s ease',
            opacity: 0.7,
          }}
          onMouseEnter={e => { e.target.style.opacity = '1'; e.target.style.background = '#1e1e2e'; e.target.style.borderColor = '#6c63ff'; }}
          onMouseLeave={e => { e.target.style.opacity = '0.7'; e.target.style.background = '#16161f'; e.target.style.borderColor = config.border; }}
        >
          ⊕
        </button>
      ) : data.childCount > 0 ? (
        <button
          className={`fractal-collapse-btn ${data.isCollapsed && data.descendantCount > 0 ? 'collapsed-deep' : ''}`}
          onClick={(e) => { e.stopPropagation(); data.onToggleCollapse?.(data.nodeId); }}
          title={data.isCollapsed ? `Expand (${data.descendantCount || data.childCount} hidden nodes)` : 'Collapse children'}
          style={{
            position: 'absolute', bottom: -16, left: '50%',
            transform: 'translateX(-50%)',
            background: data.isCollapsed ? 'rgba(108,99,255,0.15)' : config.bg,
            border: `1px solid ${data.isCollapsed ? 'rgba(108,99,255,0.4)' : config.border}`,
            borderRadius: 10, padding: '1px 8px',
            fontSize: 9, fontWeight: 700,
            color: data.isCollapsed ? '#8b83ff' : config.color,
            cursor: 'pointer', zIndex: 2, fontFamily: 'var(--font-mono)',
            display: 'flex', alignItems: 'center', gap: 3,
            transition: 'all 0.15s ease',
          }}
        >
          <span style={{ fontSize: 8 }}>{data.isCollapsed ? '+' : '▾'}</span>
          {data.isCollapsed ? (data.descendantCount || data.childCount) : data.childCount}
        </button>
      ) : null}

      {/* Unexplored glow effect */}
      {isUnexplored && isInRange && !isDimmed && (
        <div className="fractal-glow" style={{
          position: 'absolute', bottom: -2, left: '10%', right: '10%',
          height: 4, borderRadius: 2,
          background: 'linear-gradient(90deg, transparent, rgba(108,99,255,0.4), transparent)',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
});

export default IdeaNode;
